import { configureLocalization } from '@lit/localize/init/runtime.js';
import {
  allLocales,
  sourceLocale,
  targetLocales,
} from '../generated/locale-codes';

export type LocaleCode = (typeof allLocales)[number];
export type LocalePreference = LocaleCode | 'system';

const LOCALE_STORAGE_KEY = 'kv.locale';
const DEFAULT_LOCALE_PREFERENCE: LocalePreference = 'system';

const configuredLocalization = configureLocalization({
  sourceLocale,
  targetLocales,
  loadLocale: (locale) => {
    switch (locale) {
      case 'de':
        return import('../generated/locales/de');
      default:
        throw new Error(`Unsupported locale: ${locale}`);
    }
  },
});

export const getLocale = configuredLocalization.getLocale;
export const setLocale = configuredLocalization.setLocale;

export function isLocaleCode(value: unknown): value is LocaleCode {
  return (
    typeof value === 'string' &&
    (allLocales as readonly string[]).includes(value)
  );
}

export function isLocalePreference(value: unknown): value is LocalePreference {
  return value === 'system' || isLocaleCode(value);
}

export function readLocalePreference(): LocalePreference {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocalePreference(stored) ? stored : DEFAULT_LOCALE_PREFERENCE;
  } catch (_) {
    return DEFAULT_LOCALE_PREFERENCE;
  }
}

export function persistLocalePreference(value: LocalePreference): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, value);
  } catch (_) {
    /* no-op */
  }
}

export function resolveBrowserLocale(
  languages: readonly string[] = navigator.languages
): LocaleCode {
  for (const language of languages) {
    const normalized = language.toLowerCase();
    for (const locale of allLocales) {
      if (normalized === locale || normalized.startsWith(`${locale}-`)) {
        return locale;
      }
    }
  }
  return sourceLocale;
}

export function resolveLocalePreference(
  preference: LocalePreference,
  languages?: readonly string[]
): LocaleCode {
  return preference === 'system' ? resolveBrowserLocale(languages) : preference;
}
